import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Button } from '@deepguide-ai/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function NotionConfigModal({ open, onOpenChange, integration, workspaceId, onComplete }: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false);
  const integrationId = integration?.id ?? '';
  const status = useQuery({ ...trpc.integrations.getNotionConnectionStatus.queryOptions({ integrationId }), enabled: open && !!integrationId });
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='sm:max-w-xl'>
      <DialogHeader>
        <DialogTitle>Notion evidence</DialogTitle>
        <DialogDescription>Collect workspace people and bots for access reviews.</DialogDescription>
      </DialogHeader>
      <ScrollArea type='scroll' className='max-h-[65vh]' viewClassName='max-h-[65vh] [&>div]:!block'>
      <div className='space-y-3 px-6 pb-4 text-sm'>
        {integrationId && status.isLoading ? <p role='status'>Loading Notion connection…</p> : null}
        {status.error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive'>{status.error.message}</p>
          <Button variant='outline' disabled={status.isFetching} onClick={() => void status.refetch()}>Retry loading</Button>
        </div> : status.data ? <p>Connected workspace: {status.data.workspaceName ?? status.data.providerAccount}</p> : null}
        <p className='text-muted-foreground'>Use Scan on the Notion card to collect evidence and view progress and results. Use Access reviews to review collected accounts. Connecting alone does not collect evidence.</p>
        <p className='text-muted-foreground'>The public API excludes guests and does not expose workspace roles or account status. MFA and SSO enforcement require separate evidence.</p>
        <p className='text-muted-foreground'>Your Notion app must have user-information read capability. Reconnect to enable secure token renewal if you connected before this update.</p>
        {status.data?.lastError ? <p role='alert' className='text-destructive'>{status.data.lastError}</p> : null}
        <p className='text-muted-foreground'>Disconnect from the integration card to stop future collection. Collected evidence is retained. Revoke app access in Notion separately.</p>
      </div>
      </ScrollArea>
      <DialogFooter>
        <Button disabled={connecting} onClick={async () => {
          setConnecting(true);
          try { await NotionProvider.handleConnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) }); }
          catch { setConnecting(false); }
        }}>{connecting ? 'Connecting…' : integrationId ? 'Reconnect Notion' : 'Connect Notion'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

export const NotionProvider: IntegrationProvider = {
  id: 'Notion', name: 'Notion', description: 'Workspace member inventory',
  Icon: () => <img src={vendorLogoPath('notion')} alt='' className='size-6 object-contain' />,
  ConfigModal: NotionConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    { id: 'access', name: 'Access inventory', description: 'Read visible workspace people and bots', enabled: true },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.initNotionAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start Notion authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'notion-auth', provider: 'Notion', onSuccess, onFinish,
      onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow popups to connect Notion.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.completeNotionAuth.mutationOptions()).execute({ code, state });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getNotionConnectionStatus.queryKey() }),
    ]);
    return result;
  },
  syncData: async () => { throw new Error('Use Scan on the Notion integration card to collect access evidence.'); },
};
